import React, { useEffect, useState, useCallback, useMemo } from "react";
import { getFollowupsWithParams } from "../../services/followupsReportService";
import "./FollowupsReportLayer.css";

const ITEMS_PER_PAGE = 10;

const FollowupsReportLayer = () => {
  const [data, setData] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState(""); // Local search state

  // Consolidated filter state
  const [filters, setFilters] = useState({
    userId: "",
    leadId: "",
    role: "", // New role filter
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const fetchData = useCallback(async (isInitial = false) => {
    setLoading(true);
    try {
      const params = {};
      if (filters.userId) params.user_id = filters.userId;
      if (filters.leadId) params.lead_id = filters.leadId;
      if (filters.role) params.role = filters.role;

      const res = await getFollowupsWithParams(params);
      setData(res?.data || []);
      setSummary(res?.summary || {});
      
      if (!isInitial) setPage(1); 
    } catch (error) {
      console.error("Failed to fetch followups:", error);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchData(true);
  }, [fetchData]);

  const resetFilters = () => {
    setFilters({ userId: "", leadId: "", role: "" });
    setSearchTerm("");
    setPage(1);
  };

  const getRoleLabel = (role) => {
    const roleMap = {
      tm: "Team Manager",
      sales: "Sales Executive",
    };
    return roleMap[role] || role || "-";
  };

  // 🔥 Local Search Filter: Filters Name or Phone Number
  const filteredData = useMemo(() => {
    if (!searchTerm) return data;
    const lowerTerm = searchTerm.toLowerCase().trim();
    
    return data.filter((item) => {
      const userName = item.user?.name?.toLowerCase() || "";
      const leadName = item.lead?.name?.toLowerCase() || "";
      const phone = item.phone || "";
      
      return userName.includes(lowerTerm) || leadName.includes(lowerTerm) || phone.includes(lowerTerm);
    });
  }, [data, searchTerm]);

  // Memoized Pagination Calculations
  const { currentTableData, totalPages } = useMemo(() => {
    const total = Math.ceil(filteredData.length / ITEMS_PER_PAGE);
    const sliced = filteredData.slice(
      (page - 1) * ITEMS_PER_PAGE,
      page * ITEMS_PER_PAGE
    );
    return { currentTableData: sliced, totalPages: total };
  }, [filteredData, page]);

  return (
    <div className="follow-container">
      <h5>Followups Report Dashboard</h5>

      <div className="filters">
        {/* Role Filter Dropdown */}
        {/* <select name="role" value={filters.role} onChange={handleInputChange} className="filter-select">
          <option value="">All Roles</option>
          <option value="tm">Team Manager</option>
          <option value="sales">Sales Executive</option>
        </select> */}

        {/* Global Search Input (Name/Phone) */}
        <input
          type="text"
          placeholder="Search Name or Mobile..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setPage(1); // Reset to first page on search
          }}
          className="search-input"
        />

        {/* <input
          type="number"
          name="userId"
          placeholder="User ID"
          value={filters.userId}
          onChange={handleInputChange}
        /> */}

        <button onClick={() => fetchData()} disabled={loading} className="apply-btn">
          {loading ? "Loading..." : "Apply"}
        </button>
        <button onClick={resetFilters} className="reset" disabled={loading}>
          Reset
        </button>
      </div>

      <div className="summary">
        <div className="card">
          <strong>Total Followups:</strong> {summary.total_followups || 0}
        </div>
      </div>

      <div className="table-wrapper">
        <table className="report-table">
          <thead>
            <tr>
              <th>User</th>
              <th>Role</th>
              <th>Lead</th>
              <th>Phone</th>
              <th>Followup</th>
              <th>Note</th>
              <th>Interest</th>
              <th>Date</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="8" className="loading">Fetching data...</td>
              </tr>
            ) : currentTableData.length > 0 ? (
              currentTableData.map((item) => (
                <tr key={item.id}>
                  <td>{item.user?.name || "-"}</td>
                  <td>{getRoleLabel(item.user?.role)}</td>
                  <td>{item.lead?.name || "-"}</td>
                  <td>{item.phone}</td>
                  <td>{item.followup_text || "-"}</td>
                  <td className="note-cell">{item.note_text || "-"}</td>
                  <td>
                    <span className={`interest-badge ${item.conversion_data?.interest_level}`}>
                      {item.conversion_data?.interest_level || item.conversion_data?.interest || "-"}
                    </span>
                  </td>
                  <td>{new Date(item.created_at).toLocaleDateString()}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="8" className="no-data">No results found for your search.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {!loading && totalPages > 1 && (
        <div className="pagination">
          <button disabled={page === 1} onClick={() => setPage((p) => p - 1)}>Prev</button>
          <span>Page {page} of {totalPages}</span>
          <button disabled={page === totalPages} onClick={() => setPage((p) => p + 1)}>Next</button>
        </div>
      )}
    </div>
  );
};

export default FollowupsReportLayer;